import { OrderListDeliveryDay } from "../types/orderListTypes";

const deliveryDayNames: OrderListDeliveryDay[] = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
];

export const getSettingsForDeliveryDate = <T extends { deliveryDays: string[] }>(
  settings: T[],
  deliveryDate: string
): T[] => {
  const date = new Date(`${deliveryDate}T00:00:00`);
  return settings.filter(setting => setting.deliveryDays.includes(deliveryDayNames[date.getDay()]));
};

const dayNames: OrderListDeliveryDay[] = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
];

export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const moveToConfiguredDate = (
  dateText: string,
  deliveryDays: OrderListDeliveryDay[],
  direction: 1 | -1
): string => {
  const date = new Date(`${dateText}T00:00:00`);
  const configured = new Set(deliveryDays);
  for (let offset = 1; offset <= 7; offset += 1) {
    const candidate = new Date(date);
    candidate.setDate(candidate.getDate() + direction * offset);
    if (configured.has(dayNames[candidate.getDay()])) {
      return formatLocalDate(candidate);
    }
  }
  return dateText;
};

export const getCurrentOrNextConfiguredDate = (
  dateText: string,
  deliveryDays: OrderListDeliveryDay[]
): string => {
  const date = new Date(`${dateText}T00:00:00`);
  const configured = new Set(deliveryDays);
  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = new Date(date);
    candidate.setDate(candidate.getDate() + offset);
    if (configured.has(dayNames[candidate.getDay()])) {
      return formatLocalDate(candidate);
    }
  }
  return dateText;
};

export interface OrderListDeliveryRequest<T> {
  setting: T;
  deliveryDate: string;
}

export const getUpcomingDeliveries = <T extends { deliveryDays: OrderListDeliveryDay[] }>(
  settings: T[],
  anchorDate: string
): OrderListDeliveryRequest<T>[] => settings
  .filter(setting => setting.deliveryDays.length > 0)
  .map(setting => ({
    setting,
    deliveryDate: getCurrentOrNextConfiguredDate(anchorDate, setting.deliveryDays),
  }))
  .sort((left, right) =>
    left.deliveryDate.localeCompare(right.deliveryDate) ||
    String((left.setting as T & { name?: string }).name ?? "").localeCompare(
      String((right.setting as T & { name?: string }).name ?? ""),
      undefined,
      { sensitivity: "base" }
    )
  );

export const getOrderListDeliveryRequests = <T extends { id: string; deliveryDays: OrderListDeliveryDay[] }>(
  settings: T[],
  anchorDate: string,
  selectedSettingId: string,
  allCategoriesId: string
): OrderListDeliveryRequest<T>[] => selectedSettingId === allCategoriesId
  ? getUpcomingDeliveries(settings, anchorDate)
  : settings
    .filter(setting => setting.id === selectedSettingId && setting.deliveryDays.length > 0)
    .map(setting => ({
      setting,
      deliveryDate: getCurrentOrNextConfiguredDate(anchorDate, setting.deliveryDays),
    }));